import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { X } from 'lucide-react';
import { adminApi } from '../../repositories/admin';
import MultiSelect from '../../PitchFormPage/components/MultiSelect/MultiSelect';
import Button from '../ui/Button';
import { Skeleton } from '../ui/skeleton';

interface AddDspOrganizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (
    name: string,
    labelIds: number[]
  ) => void;
}

interface Label {
  id: number;
  name: string;
}

const AddDspOrganizationModal: React.FC<AddDspOrganizationModalProps> = ({
  isOpen,
  onClose,
  onSave,
}) => {
  const [dspName, setDspName] = useState('');
  const [selectedLabelKeys, setSelectedLabelKeys] = useState<string[]>([]);
  const [availableLabels, setAvailableLabels] = useState<Label[]>([]);
  const [labelsLoading, setLabelsLoading] = useState(false);

  // Create unique keys for each label using ID
  const labelToKey = (label: Label) => `${label.id}`;
  const keyToLabelId = (key: string) => parseInt(key, 10);

  // Create display map: key -> label name for display
  const displayMap: Record<string, string> = {};
  availableLabels.forEach(label => {
    displayMap[labelToKey(label)] = label.name;
  });

  useEffect(() => {
    if (isOpen) {
      fetchLabels();
      // Reset form when modal opens
      setDspName('');
      setSelectedLabelKeys([]);
    }
  }, [isOpen]);

  const fetchLabels = async () => {
    setLabelsLoading(true);
    try {
      const response = await adminApi.getLabels();
      setAvailableLabels(response.data.data);
    } catch (error) {
      console.error('Failed to fetch labels:', error);
    } finally {
      setLabelsLoading(false);
    }
  };

  const handleClose = () => {
    setDspName('');
    setSelectedLabelKeys([]);
    onClose();
  };

  const handleSave = async () => {
    try {
      if (!dspName || selectedLabelKeys.length === 0) {
        return Swal.fire('Error', 'Please fill in all fields', 'error');
      }

      // Convert selected label keys to IDs
      const labelIds = selectedLabelKeys
        .filter(key => key !== 'Select All')
        .map(keyToLabelId)
        .filter(id => !isNaN(id));

      onSave(dspName, labelIds);
      handleClose();
    } catch (error) {
      console.error(error);
      Swal.fire('Error', 'Error adding DSP organization', 'error');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="relative w-full max-w-md p-6 bg-white shadow-lg rounded-2xl">
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-1 transition-colors rounded-md text-neutral-400 hover:text-primary-700 hover:bg-primary-50 focus:outline-none focus-visible:border-primary-700 border border-transparent"
          type="button"
        >
          <X />
        </button>
        <h2 className="mb-6 text-center text-lg font-semibold text-black">Add DSP Organization</h2>

        <div>
          <label className="block mb-2 text-sm font-medium text-neutral-700">DSP Name</label>
          <input
            type="text"
            value={dspName}
            onChange={(e) => setDspName(e.target.value)}
            placeholder="Enter DSP name"
            required
            className="w-full px-3 py-2 text-sm text-black transition-colors bg-white border rounded-md outline-none border-neutral-300 placeholder-neutral-400 focus:border-primary-500"
          />
        </div>

        <div className="mt-4">
          {/* Until the labels arrive the picker would offer only "Select All". */}
          {labelsLoading ? (
            <div aria-busy="true">
              <span className="block mb-2 text-sm font-medium text-neutral-700">Select Labels</span>
              <Skeleton className="w-full h-[38px]" />
            </div>
          ) : (
          <MultiSelect
            label="Select Labels"
            options={['Select All', ...availableLabels.map(labelToKey)]}
            value={selectedLabelKeys}
            onChange={setSelectedLabelKeys}
            placeholder="Select labels"
            displayMap={displayMap}
          />
          )}
        </div>

        <div className="mt-6">
          <Button
            onClick={handleSave}
            className="w-full"
          >
            Add Organization
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddDspOrganizationModal;
